/**
 * Compile a single GLSL shader.
 * @param gl WebGL context
 * @param type WebGL shader type (VERTEX_SHADER or FRAGMENT_SHADER)
 * @param source GLSL source
 */
export function compileShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string,
): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Failed to create WebGL shader');

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const info = gl.getShaderInfoLog(shader) ?? 'unknown';
    gl.deleteShader(shader);
    // Henrik: surface compile errors directly; it saves time when tweaking shader code.
    throw new Error(`Failed to compile WebGL shader: ${info}`);
  }

  return shader;
}

/**
 * Link a WebGL program from a vertex and fragment shader.
 * @param gl WebGL context
 * @param vs Vertex shader
 * @param fs Fragment shader
 */
export function linkProgram(
  gl: WebGLRenderingContext,
  vs: WebGLShader,
  fs: WebGLShader,
): WebGLProgram {
  const program = gl.createProgram();
  if (!program) throw new Error('Failed to create WebGL program');

  gl.attachShader(program, vs);
  gl.attachShader(program, fs);
  gl.linkProgram(program);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const info = gl.getProgramInfoLog(program) ?? 'unknown';
    gl.deleteProgram(program);
    throw new Error(`Failed to link WebGL program: ${info}`);
  }

  gl.deleteShader(vs);
  gl.deleteShader(fs);

  return program;
}
